import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { UserMarketplaceStats } from '@/types/residents/marketplaceListing';
import { StarRating } from './StarRating';
import { StatItem } from './StatItem';
import { SVG_COMPONENTS } from '@/assets/admin';

const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

interface ProfileStatisticsProps {
  stats: UserMarketplaceStats;
  onFollowingPress?: () => void;
  onRatingPress?: () => void;
}

export const ProfileStatistics: React.FC<ProfileStatisticsProps> = ({
  stats,
  onFollowingPress,
  onRatingPress,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      gap: 14,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '500',
      color: mc.textPrimary,
      lineHeight: 24,
    },
    numbersContainer: {
      gap: 10,
    },
    topRow: {
      backgroundColor: mc.cardBackground,
      borderRadius: 16,
      paddingVertical: 14,
      paddingHorizontal: 16,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    statItem: {
      minWidth: 52,
    },
    bottomRow: {
      flexDirection: 'row',
      gap: 10,
    },
    followingCard: {
      flex: 1,
      backgroundColor: mc.cardBackground,
      borderRadius: 16,
      padding: 16,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    followingLabel: {
      fontSize: 14,
      fontWeight: '400',
      color: mc.textSecondary,
      lineHeight: 21,
    },
    followingValue: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    followingNumber: {
      fontSize: 14,
      fontWeight: '500',
      color: mc.textPrimary,
      lineHeight: 21,
    },
    ratingCard: {
      flex: 1,
      backgroundColor: mc.cardBackground,
      borderRadius: 16,
      padding: 16,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    ratingValue: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    ratingNumber: {
      fontSize: 14,
      fontWeight: '500',
      color: mc.textPrimary,
      lineHeight: 21,
    },
  }), [mc]);

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>Statistics</Text>
      
      <View style={styles.numbersContainer}>
        {/* Top Row - Bought, Sold, Followers */}
        <View style={styles.topRow}>
          <StatItem value={stats.bought} label="Bought" containerStyle={styles.statItem} />
          <StatItem value={stats.sold} label="Sold" containerStyle={styles.statItem} />
          <StatItem value={stats.followers} label="Followers" containerStyle={styles.statItem} />
        </View>
        
        {/* Bottom Row - Following and Rating */}
        <View style={styles.bottomRow}>
          {/* Following */}
          <TouchableOpacity 
            style={styles.followingCard}
            onPress={onFollowingPress}
            activeOpacity={0.8}
          >
            <Text style={styles.followingLabel}>Following</Text>
            <View style={styles.followingValue}>
              <Text style={styles.followingNumber}>{stats.following}</Text>
              <ChevronRightIcon width={20} height={20} color={mc.textMuted} />
            </View>
          </TouchableOpacity>
          
          {/* Rating */}
          <TouchableOpacity 
            style={styles.ratingCard}
            onPress={onRatingPress}
            activeOpacity={0.8}
          >
            <StarRating rating={stats.rating} size={16} />
            <View style={styles.ratingValue}>
              <Text style={styles.ratingNumber}>{stats.reviewCount}</Text>
              <ChevronRightIcon width={20} height={20} color={mc.textMuted} />
            </View>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

